import { useState, useEffect } from "react";
import "./Layout.scss";
import { Layout, Menu, Row, Col, Card, Avatar, Spin, Grid, Drawer } from "antd";
import {
  AppstoreOutlined,
  InboxOutlined,
  UserOutlined,
  ShopOutlined,
  UsergroupAddOutlined,
  HomeOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import DrawerComponent from "../../components/Drawer";
import { useLocation, useNavigate } from "react-router-dom";
import TopBar from "../../pages/Marketplace/global_components/TopBar";

import useAppStore from "../../store/useAppStore";

// Notifications
import {
  sendNativeNotification,
  toastNotification,
} from "../../actions/notificationHelpers";
import useVisibilityChange from "../../utils/hooks/useVisibilityChange";
import { setupNotifications } from "../../utils/services/firebase";

const { Content, Footer } = Layout;
const { Meta } = Card;
const { useBreakpoint } = Grid;

export default function LayoutPage(props) {
  const { children } = props;

  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.id;
  let role = localStorage.getItem("role");
  let navigate = useNavigate();
  const location = useLocation();

  const [visible, setVisible] = useState(false);
  const screens = useBreakpoint();
  const [menuDrawerVisible, setMenuDrawerVisible] = useState(false);

  const loading = useAppStore((state) => state.loading);
  const initialized = useAppStore((state) => state.initialized);
  const notifications = useAppStore((state) => state.notifications);
  const cart = useAppStore((state) => state.cart);
  const unreadCount = useAppStore((state) => state.unreadCount);
  const cartCount = useAppStore((state) => state.cartCount);
  const initialize = useAppStore((state) => state.initialize);
  const syncData = useAppStore((state) => state.syncData);

  const isMarketplaceRoute = location.pathname.startsWith("/marketplace");
  const isRootRoute = location.pathname === "/";
  const shouldShowTopBar =
    isMarketplaceRoute ||
    isRootRoute ||
    role === "1" ||
    role === "4" ||
    role === "6" ||
    role === "3" ||
    role === "5";

  const handleGlobalSearch = (searchTerm) => {
    if (searchTerm.trim()) {
      navigate(`/marketplace?search=${encodeURIComponent(searchTerm)}`);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const getPageTitle = () => {
    if (location.pathname === "/") {
      return "Bienvenido a EP&O";
    }
    if (location.pathname.startsWith("/marketplace")) {
      return userId ? "Marketplace" : "Bienvenido a EP&O";
    }
    if (location.pathname.startsWith("/@p/")) return "Mi Perfil";
    if (location.pathname.startsWith("/dashboard")) return "Dashboard";
    if (location.pathname.startsWith("/orders")) return "Mis Órdenes";
    return "";
  };

  useEffect(() => {
    initialize(userId, role);
  }, [userId, role, initialize]);

  useEffect(() => {
    if (!userId || !initialized) {
      return;
    }

    const interval = setInterval(() => {
      syncData();
    }, 60000);

    return () => {
      clearInterval(interval);
    };
  }, [userId, initialized, syncData]);
  const isForeground = useVisibilityChange();

  useEffect(() => {
    if (!userId) return;

    setupNotifications((message) => {
      if (isForeground) {
        toastNotification({
          title: message.data.title || "Notification",
          description: message.data.body || "You have a new notification",
          status: "info",
        });
      } else {
        sendNativeNotification({
          title: message.data.title || "Notification",
          body: message.data.body || "You have a new notification",
        });
      }
    });
  }, [isForeground, userId]);

  const menuItems = [
    ...(role === "1"
      ? [
        {
          key: "/dashboard",
          icon: <HomeOutlined />,
          label: (
            <div
              onClick={() => {
                navigate("/dashboard");
                setMenuDrawerVisible(false);
              }}
            >
              Inicio
            </div>
          ),
        },
        {
          key: "usuarios",
          icon: <UsergroupAddOutlined />,
          label: "Usuarios",
          children: [
            {
              key: "/@u",
              label: (
                <div
                  onClick={() => {
                    navigate("/@u");
                    setMenuDrawerVisible(false);
                  }}
                >
                  Lista de Usuarios
                </div>
              ),
            },
            {
              key: "/@a-@c",
              label: (
                <div
                  onClick={() => {
                    navigate("/@a-@c");
                    setMenuDrawerVisible(false);
                  }}
                >
                  Administración de Clientes
                </div>
              ),
            },
          ],
        },
        {
          key: "mp-admin",
          icon: <ShopOutlined />,
          label: "Marketplace",
          children: [
            {
              key: "/marketplace",
              label: (
                <div
                  onClick={() => {
                    navigate("/marketplace");
                    setMenuDrawerVisible(false);
                  }}
                >
                  Inicio
                </div>
              ),
            },
            {
              key: `/marketplace/${user?.id}/orders`,
              label: (
                <div
                  onClick={() => {
                    navigate(`/marketplace/${user?.id}/orders`);
                    setMenuDrawerVisible(false);
                  }}
                >
                  Órdenes de compra
                </div>
              ),
            },
            {
              key: "/marketplace/admin/categories",
              label: (
                <div
                  onClick={() => {
                    navigate("/marketplace/admin/categories");
                    setMenuDrawerVisible(false);
                  }}
                >
                  Administrar Categorías
                </div>
              ),
            },
            {
              key: "/marketplace/admin/product-validation",
              label: (
                <div
                  onClick={() => {
                    navigate("/marketplace/admin/product-validation");
                    setMenuDrawerVisible(false);
                  }}
                >
                  Validar Productos
                </div>
              ),
            },
          ],
        },
        {
          key: "marketing-admin",
          icon: <AppstoreOutlined />,
          label: "Marketing",
          children: [
            {
              key: "/marketing/marketplace",
              label: (
                <div
                  onClick={() => {
                    navigate("/marketing/marketplace");
                    setMenuDrawerVisible(false);
                  }}
                >
                  Mercadotecnia
                </div>
              ),
            },
            {
              key: "/marketing/especiales",
              label: (
                <div
                  onClick={() => {
                    navigate("/marketing/especiales");
                    setMenuDrawerVisible(false);
                  }}
                >
                  Especiales
                </div>
              ),
            },
          ],
        },
      ]
      : []),

    ...(["1", "3", "5"].includes(role)
      ? [
        {
          key: "ordenes-admin",
          icon: <InboxOutlined />,
          label: "Órdenes",
          children: [
            {
              key: "/orders/all/1",
              label: (
                <div
                  onClick={() => {
                    navigate("/orders/all/1");
                    setMenuDrawerVisible(false);
                  }}
                >
                  Órdenes generales
                </div>
              ),
            },
            {
              key: "/orders/express",
              label: (
                <div
                  onClick={() => {
                    navigate("/orders/express");
                    setMenuDrawerVisible(false);
                  }}
                >
                  Órdenes express
                </div>
              ),
            },
            {
              key: "/orders/finished",
              label: (
                <div
                  onClick={() => {
                    navigate("/orders/finished");
                    setMenuDrawerVisible(false);
                  }}
                >
                  Todos los servicios
                </div>
              ),
            },
          ],
        },
      ]
      : []),

    ...(role === "6"
      ? [
        {
          key: "/orders/all/1",
          icon: <InboxOutlined />,
          label: (
            <div
              onClick={() => {
                navigate("/orders/all/1");
                setMenuDrawerVisible(false);
              }}
            >
              Órdenes
            </div>
          ),
        },
        {
          key: "mp-supplier",
          icon: <ShopOutlined />,
          label: "Marketplace",
          children: [
            {
              key: `/marketplace/suppliers/${user?.id}`,
              label: (
                <div
                  onClick={() => {
                    navigate(`/marketplace/suppliers/${user?.id}`);
                    setMenuDrawerVisible(false);
                  }}
                >
                  Perfil
                </div>
              ),
            },
            {
              key: `/marketplace/${user?.id}/all-products`,
              label: (
                <div
                  onClick={() => {
                    navigate(`/marketplace/${user?.id}/all-products`);
                    setMenuDrawerVisible(false);
                  }}
                >
                  Mis productos
                </div>
              ),
            },
            {
              key: `/marketplace/${user?.id}/orders`,
              label: (
                <div
                  onClick={() => {
                    navigate(`/marketplace/${user?.id}/orders`);
                    setMenuDrawerVisible(false);
                  }}
                >
                  Órdenes de compra
                </div>
              ),
            },
          ],
        },
        {
          key: `/perfil/${user?.id}`,
          icon: <UserOutlined />,
          label: (
            <div
              onClick={() => {
                navigate(`/@p/${user?.id}`);
                setMenuDrawerVisible(false);
              }}
            >
              Perfil
            </div>
          ),
        },
      ]
      : []),

    ...(role === "4"
      ? [
        {
          key: "/orders/all/1",
          icon: <InboxOutlined />,
          label: (
            <div
              onClick={() => {
                navigate("/orders/all/1");
                setMenuDrawerVisible(false);
              }}
            >
              Órdenes
            </div>
          ),
        },
        {
          key: "mp-client",
          icon: <ShopOutlined />,
          label: "Marketplace",
          children: [
            {
              key: "/marketplace",
              label: (
                <div
                  onClick={() => {
                    navigate("/marketplace");
                    setMenuDrawerVisible(false);
                  }}
                >
                  Inicio
                </div>
              ),
            },
            {
              key: "/marketplace/shopping-cart",
              label: (
                <div
                  onClick={() => {
                    navigate("/marketplace/shopping-cart");
                    setMenuDrawerVisible(false);
                  }}
                >
                  Carrito
                </div>
              ),
            },
            {
              key: "/marketplace/orders",
              label: (
                <div
                  onClick={() => {
                    navigate("/marketplace/orders");
                    setMenuDrawerVisible(false);
                  }}
                >
                  Mis compras
                </div>
              ),
            },
          ],
        },
        {
          key: `/perfil/${user?.id}`,
          icon: <UserOutlined />,
          label: (
            <div
              onClick={() => {
                navigate(`/@p/${user?.id}`);
                setMenuDrawerVisible(false);
              }}
            >
              Perfil
            </div>
          ),
        },
      ]
      : []),
    ...(['1', '2', '3', '5'].includes(role) ? [
      {
        key: '/suppliers/',
        icon: <HomeOutlined />,
        label: <div onClick={() => { navigate("/suppliers/"); setMenuDrawerVisible(false); }}>Directorio de proveedores</div>,
      }
    ] : []),
    ...(['1', '2', '3', '5'].includes(role) ? [
      {
        key: "mp-contabilidad",
        icon: <ShopOutlined />,
        label: "Contabilidad",
        children: [
          {
            key: `/accounts/`,
            label: (
              <div
                onClick={() => {
                  navigate("/accounts/");
                  setMenuDrawerVisible(false);
                }}>
                Cuentas de Banco
              </div>
            ),
          },
          {
            key: `/expenses/`,
            label: (
              <div
                onClick={() => {
                  navigate(`/expenses/`);
                  setMenuDrawerVisible(false);
                }}
              >
                Gastos Finacieros 
              </div>
            ),
          }
        ],
      }     

    ] : []),



  ];

  const footerContent = (
    <div className="footer_cart">
      <div>
        <p>Subtotal:</p>
        <p>${cart?.total || 0}</p>
        <p>Artículos: {cartCount}</p>
      </div>
    </div>
  );

  const MenuButton = () => {
    if (!userId) return null;
    if (menuDrawerVisible) return null;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "40px",
          height: "40px",
          backgroundColor: "#0F5197",
          color: "white",
          borderRadius: "6px",
          cursor: "pointer",
          marginRight: "16px",
          transition: "all 0.3s ease",
          border: "none",
          flexShrink: 0,
        }}
        onClick={() => setMenuDrawerVisible(true)}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#1a75bb";
          e.currentTarget.style.transform = "scale(1.05)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "#0F5197";
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        <MenuOutlined style={{ fontSize: "18px" }} />
      </div>
    );
  };

  // Permitir que usuarios invitados vean el marketplace
  if (role === "" && !isMarketplaceRoute) {
    return (
      <Row justify="center" align="middle" style={{ height: "100%" }}>
        <Col xs={24} style={{ textAlign: "center" }}>
          <Spin size="large" tip="Validando" />
        </Col>
      </Row>
    );
  }

  // Si es marketplace y no hay role, tratamos como invitado
  const effectiveRole = role || "guest";

  if (userId && loading) {
    return (
      <Row justify="center" align="middle" style={{ minHeight: "100vh" }}>
        <Col>
          <Spin size="large" tip="Cargando aplicación..." />
        </Col>
      </Row>
    );
  }

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Drawer para el menú lateral*/}
      {userId && (
        <Drawer
          title={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "white",
              }}
            >
              <span style={{ color: "white", fontWeight: "600" }}>
                Menú Principal
              </span>
            </div>
          }
          placement="left"
          onClose={() => setMenuDrawerVisible(false)}
          open={menuDrawerVisible}
          width={screens.lg ? "320px" : "85%"}
          style={{ zIndex: 1002 }}
          styles={{
            header: {
              backgroundColor: "#0A3A6B",
              borderBottom: "1px solid #08305c",
              padding: "16px 24px",
            },
            body: {
              padding: 0,
              backgroundColor: "#0F5197",
            },
          }}
          closeIcon={
            <div
              style={{
                color: "white",
                fontSize: "16px",
                transition: "all 0.3s ease",
              }}
            >
              ✕
            </div>
          }
        >
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            defaultOpenKeys={[location.pathname.split("/")[1]]}
            className="my-custom-menu"
            items={menuItems}
            style={{
              color: "white",
              backgroundColor: "#0F5197",
              borderRight: "none",
              height: "100%",
              paddingTop: "8px",
            }}
          />
        </Drawer>
      )}

      <Layout>
        {shouldShowTopBar && (
          <div className="global-topbar-container">
            <TopBar
              isCategory={isMarketplaceRoute || isRootRoute}
              onSearch={handleGlobalSearch}
              title={getPageTitle()}
              onLogout={handleLogout}
              menuButton={<MenuButton />}
            />
          </div>
        )}

        <Content
          className="site-layout-background"
          style={{
            margin: 0,
            padding:
              shouldShowTopBar && (isMarketplaceRoute || isRootRoute) ? 0 : 24,
            minHeight: 280,
            backgroundColor:
              isMarketplaceRoute || isRootRoute ? "white" : "#f5f5f5",
          }}
        >
          {children}
        </Content>

        {!(isMarketplaceRoute || isRootRoute) && (
          <Footer style={{ textAlign: "center" }}>
            Copyright ©2026 Created by EP&O
          </Footer>
        )}
      </Layout>

      {userId && (
        <DrawerComponent
          title={"Mi carrito"}
          footerContent={footerContent}
          visible={visible}
          setVisible={setVisible}
        >
          {cart?.products?.map((prod, index) => (
            <Col key={index}>
              <Card
                actions={[
                  <label>Precio(C/U): ${prod.part_cost}</label>,
                  <label>Cant: {prod.qty}</label>,
                ]}
              >
                <Meta
                  avatar={
                    <Avatar src={`https://api.epno-app.com${prod.image}`} />
                  }
                  title={prod.name}
                  description={`Part.No: ${prod.part_no}`}
                />
              </Card>
            </Col>
          ))}
        </DrawerComponent>
      )}
    </Layout>
  );
}
